import FormStruct from "@/components/Form/FormStruct/FormStruct";
import FormValidators from "@/components/Form/Validation/FormValidators";
import WrappedText from "@/components/WrappedText/WrappedText";
import i18n from "@/i18n";
import BFChooserSelect from "@/modules/abstract-ui/forms/chooser/BFChooserSelect";
import BFDatefield from "@/modules/abstract-ui/forms/datefield/BFDatefield";
import BFInput from "@/modules/abstract-ui/forms/input/BFInput";
import {
  BFInputVariableMapEntry,
  evaluateBFInputVariables,
  generateDefaultDateVariables,
} from "@/modules/abstract-ui/forms/input/BFInputVariables";
import StringUtils from "@/utils/StringUtils";
import moment from "moment";
import { useState } from "react";
import { Field } from "react-final-form";
import { SkontoPaymentFormEntry } from "../../../RAInterfaces";

const RAInvoicePaymentSplitForm = (props: {
  currency: string;
  initialAmount: number;
  initialDate: Date;
  initialUsage: string;
  title: string;
  submitText: string;
  onSubmit: (paymentPlan: SkontoPaymentFormEntry[]) => void;
  onClose: () => void;
  variables: BFInputVariableMapEntry[];
}) => {
  const generateDateVariables = (date?: Date) => {
    const usedVariables = [
      ...(props.variables || []),
      ...generateDefaultDateVariables(date, {
        includeCurrentMonth: true,
        includeCurrentQuarter: true,
        includeCurrentYear: true,
        includeFollowQuarter: true,
        includeFollowYear: true,
        includePreviousMonth: true,
        includePreviousPreviousMonth: true,
      }),
    ];

    return usedVariables;
  };
  const [variablesForUsage] = useState(
    generateDateVariables(null).map((e) => e.key)
  );

  return (
    <FormStruct
      onAbort={props.onClose}
      onSubmit={(values) => {
        const payments: SkontoPaymentFormEntry[] = [];
        const firstDate = moment(values.startDate).startOf("day").utc(true);
        for (let i = 0; i < values.countPayments; i++) {
          const dateToUse = firstDate
            .clone()
            .add(i * values.paymentCycle, "months")
            .toDate();
          const variablesToReplace = generateDateVariables(dateToUse);

          payments.push({
            date: dateToUse,
            amount: values.amount,
            usage: evaluateBFInputVariables(values.usage, variablesToReplace),
          });
        }
        props.onSubmit(payments);
        props.onClose();
      }}
      initialValues={{
        amount: props.initialAmount || 0,
        startDate: props.initialDate || new Date(),
        usage: props.initialUsage || "",
        paymentCycle: 1,
        countPayments: 12,
      }}
      title={props.title}
      submitText={props.submitText}
      render={(formProps) => (
        <>
          <div className={`ra-invoice-payment-plan-creation-form`}>
            <div className={`description`}>
              {i18n.t(
                "ra:InvoiceForm.PaymentPlan.CreationDescription",
                "Geben Sie an welchen Zyklus und Betrag der Zahlungsplan haben soll. Sie können die Daten im Detail noch im Nachhinein bearbeiten."
              )}
            </div>

            <div className={`form`}>
              <div className={`__field`}>
                <Field
                  name={"amount"}
                  validate={FormValidators.compose(FormValidators.required())}
                >
                  {({ input, meta }) => (
                    <BFInput
                      type="priceInput"
                      label={i18n.t(
                        "ra:InvoiceForm.PaymentPlan.Creation.PaymentAmount",
                        "Betrag"
                      )}
                      prefix={StringUtils.getCurrencySymbol(props.currency)}
                      {...input}
                      {...FormValidators.getValidation(meta)}
                    />
                  )}
                </Field>
              </div>
              <div className={`__field`}>
                <Field
                  name={"usage"}
                  validate={FormValidators.compose(FormValidators.required())}
                >
                  {({ input, meta }) => (
                    <BFInput
                      label={i18n.t(
                        "ra:InvoiceForm.PaymentPlan.Creation.Usage",
                        "Verwendungszweck"
                      )}
                      info={
                        <>
                          <div className={`__h3`}>
                            {i18n.t("Global.Labels.tip", "Tipp")}:
                          </div>
                          <WrappedText
                            text={i18n.t(
                              "ra:InvoiceForm.PaymentPlan.Creation.UsageInfo",
                              "Sie können Platzhalter verwenden, um dynamische Inhalte einzufügen. Geben Sie ein '{' ein, um eine Liste verfügbarer Platzhalter anzuzeigen.\nBeispiele:\n{Rechnungsnummer}\n{Rechnungsnummer}\n{Aktuell MMM}\n{Vormonat MM}"
                            )}
                          />
                        </>
                      }
                      {...input}
                      {...FormValidators.getValidation(meta)}
                      variables={{
                        data: variablesForUsage,
                      }}
                    />
                  )}
                </Field>
              </div>

              <div className={`__field`}>
                <Field
                  name={"startDate"}
                  validate={FormValidators.compose(FormValidators.required())}
                >
                  {({ input, meta }) => (
                    <BFDatefield
                      label={i18n.t(
                        "ra:InvoiceForm.PaymentPlan.Creation.StartMonth",
                        "Beginn der Zahlung"
                      )}
                      preventNull
                      {...input}
                      {...FormValidators.getValidation(meta)}
                    />
                  )}
                </Field>
              </div>

              <div className={`__field`}>
                <Field
                  name={"paymentCycle"}
                  validate={FormValidators.compose(FormValidators.required())}
                >
                  {({ input, meta }) => (
                    <BFChooserSelect
                      hideSearch
                      label={i18n.t(
                        "ra:InvoiceForm.PaymentPlan.Creation.CycleOfPayment.Title",
                        "Zahlungszyklus"
                      )}
                      data={[
                        {
                          value: 1,
                          label: i18n.t(
                            "ra:InvoiceForm.PaymentPlan.Creation.CycleOfPayment.Monthly",
                            "Monatlich"
                          ),
                        },
                        {
                          value: 3,
                          label: i18n.t(
                            "ra:InvoiceForm.PaymentPlan.Creation.CycleOfPayment.Quarterly",
                            "Vierteljährlich"
                          ),
                        },
                        {
                          value: 6,
                          label: i18n.t(
                            "ra:InvoiceForm.PaymentPlan.Creation.CycleOfPayment.HalfYearly",
                            "Halbjährlich"
                          ),
                        },
                        {
                          value: 12,
                          label: i18n.t(
                            "ra:InvoiceForm.PaymentPlan.Creation.CycleOfPayment.Yearly",
                            "Jährlich"
                          ),
                        },
                      ]}
                      cleanable={false}
                      {...input}
                      {...FormValidators.getValidation(meta)}
                    />
                  )}
                </Field>
              </div>

              <div className={`__field`}>
                <Field
                  name={"countPayments"}
                  validate={FormValidators.compose(
                    FormValidators.required(),
                    FormValidators.min(2)
                  )}
                >
                  {({ input, meta }) => (
                    <BFInput
                      type="number"
                      step={1}
                      label={i18n.t(
                        "ra:InvoiceForm.PaymentPlan.Creation.CountPayments",
                        "Anzahl Zahlungen"
                      )}
                      {...input}
                      {...FormValidators.getValidation(meta)}
                    />
                  )}
                </Field>
              </div>
            </div>
          </div>
        </>
      )}
    />
  );
};
export default RAInvoicePaymentSplitForm;
